import {
  PackageEndpointsV2,
  type InitialLodgingAvailabilityResponse,
  type PackagesCustomizeLodgingAvailabilityRequest,
  type PackagesCustomizeLodgingShopRequest,
  type PackagesCustomizeLodgingShopResponse,
  ProductTypeFunnels,
} from "@b2bportal/core-packages";
import { Funnel } from "@b2bportal/core-types";
import { getDeviceType } from "@b2bportal/core-utilities";
import { LodgingSelectionEnum, Platform } from "@b2bportal/lodging-api";
import { PackageAvailabilityRequestEnum } from "@b2bportal/packages-api";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { HotelsShopActions } from "@tenant/hotels";
import type { PackageSearchQueryParams } from "@tenant/types";
import { PackageSearchSelectors } from "../../selectors";
import { PackagesBaseActions } from "../../slice/packages";
import type { FetchPackageHotelShopFulfillResponse } from "../../types/packagesHotels";
import type { PackagesSearch } from "../../types/packagesSearch";
import { fetchPackageSearchParams } from "../general";
import { buildLodgingChildrenAges } from "../lodgingGuestUtils";
import type { RootState } from "@/store";

// Re-export type for backwards compatibility
export type { FetchPackageHotelShopFulfillResponse };

export interface PackageHotelShopProps {
  search: PackageSearchQueryParams;
  lodgingId: string;
  packageToken: string;
}

export const fetchPackageHotelShop = createAsyncThunk<
  FetchPackageHotelShopFulfillResponse,
  { props: PackageHotelShopProps },
  { state: RootState; rejectValue: { error: string } }
>("packages/fetchHotelShop", async ({ props }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();
  const { search, lodgingId, packageToken } = props;

  // Guard against undefined packageToken to prevent 500 errors from customize call
  // Check for string "undefined" as defensive measure in case URL parsing coerces undefined to string
  if (!packageToken || packageToken === "undefined") {
    thunkAPI.dispatch(HotelsShopActions.setLodgingUnavailable(true));
    return thunkAPI.rejectWithValue({
      error: "Package token is required",
    });
  }

  const packageType =
    thunkAPI.getState().packages.base.currentActivePackageType;

  // Get the requested search to access origin/destination airport codes
  let requestedSearch = PackageSearchSelectors.getRequestedSearch(
    thunkAPI.getState()
  );

  // If we don't have requestedSearch, fetch it first (handles page refresh case)
  if (!requestedSearch?.origin || !requestedSearch?.destination) {
    const fetchedSearch = await thunkAPI
      .dispatch(fetchPackageSearchParams(search))
      .unwrap();
    requestedSearch = fetchedSearch as PackagesSearch;
  }

  // Check if this package includes flights to determine if we need dynamic date adjustments
  const funnelsToSearch = ProductTypeFunnels[packageType];
  const includesFlights = funnelsToSearch.includes(Funnel.Air);
  const availabilityRequest: PackagesCustomizeLodgingAvailabilityRequest = {
    customize: {
      bundledPackageStateToken: { value: packageToken },
      lodgingAvailabilityRequest: {
        // Only include dynamic fields for flight+hotel packages
        // For hotel-only or hotel+car packages, use exact dates
        ...(includesFlights &&
          requestedSearch && {
            dynamicFields: {
              adjustStartDateUsingLanding: {
                date: search.fromDate,
                originRegion: requestedSearch.origin.airportCode.code,
              },
              adjustEndDateUsingTakeoff: {
                date: search.untilDate,
                originRegion: requestedSearch.destination.airportCode.code,
              },
            },
          }),
        LodgingAvailabilityRequest:
          PackageAvailabilityRequestEnum.InitialRequest,
        lodgingSelection: {
          LodgingSelection: LodgingSelectionEnum.LodgingIds,
          lodgingIds: [lodgingId],
          preserveOrder: true,
        },
        stayDates: { from: search.fromDate, until: search.untilDate },
        guests: {
          adults: search.adults,
          children: buildLodgingChildrenAges(
            search.children ?? [],
            search.infantsOnLap ?? 0,
            search.infantsInSeat ?? 0
          ),
        },
        rooms: {
          numberOfRooms: search.roomCount ?? 1,
        },
        platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
      },
    },
  };

  try {
    // verify packageType
    if (search.packageType !== packageType) {
      thunkAPI.dispatch(
        PackagesBaseActions.setCurrentActivePackageType(search.packageType)
      );
    }

    thunkAPI.dispatch(HotelsShopActions.setHotelsShopPending());
    const {
      data: initialResponse,
    }: { data: InitialLodgingAvailabilityResponse } = await axiosInstance.post(
      PackageEndpointsV2.Customize,
      availabilityRequest
    );

    const lodging =
      initialResponse.lodgingAvailability.payload.availability.lodgings[0];

    thunkAPI.dispatch(HotelsShopActions.setLodging(lodging));

    // Check if we have benchmark products, otherwise set them
    const currBenchmarkProducts =
      thunkAPI.getState().packages.base.benchmarkProducts;

    if (!currBenchmarkProducts) {
      const benchmarkProducts = initialResponse.chosenProducts;
      thunkAPI.dispatch(
        PackagesBaseActions.setBenchmarkProducts({
          chosenProducts: benchmarkProducts,
        })
      );
    }

    if (lodging?.price == null) {
      thunkAPI.dispatch(HotelsShopActions.setLodgingUnavailable(true));
      return thunkAPI.rejectWithValue({
        error: "Lodging not available",
      });
    }

    // Use the fresh token from the per-property availability response so incremental
    // pricing is calculated against this hotel's current price, not the stale search-page price.
    const freshPackageState =
      initialResponse.lodgingAvailability.packageStateByLodgingId[lodgingId];
    const shopPackageToken = freshPackageState?.token?.value ?? packageToken;

    // we have the hotel info, let's get the hotel rooms now
    const shopToken = lodging.price?.opaqueShopRequest;

    const shopRequest: PackagesCustomizeLodgingShopRequest = {
      customize: {
        bundledPackageStateToken: { value: shopPackageToken },
        lodgingShopRequest: {
          opaqueShopRequest: shopToken,
          platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
        },
      },
    };

    const {
      data: shopResponse,
    }: { data: PackagesCustomizeLodgingShopResponse } =
      await axiosInstance.post(PackageEndpointsV2.Customize, shopRequest);

    // console.log("roomRes", shopResponse);

    const { payload: shopPayload, packageStateByRateId } =
      shopResponse.lodgingShop;

    //TODO: Add cache key and opaque request
    // const cacheKey = {
    //   lodgingId: search.lodgingId,
    //   fromDate: search.dateRange.from,
    //   untilDate: search.dateRange.until,
    //   guests: search.guests,
    //   rooms: search.rooms,
    // };
    // const opaqueRequest =
    //   shopTokenCache.get(cacheKey) ??
    //   availabilityTokenCache.get(cacheKey) ??
    //   lodging.price?.opaqueShopRequest;

    // if (opaqueRequest == null) {
    //   return thunkAPI.rejectWithValue({
    //     error: "Opaque shop request not available",
    //   });
    // }

    // shopTokenCache.set(cacheKey, opaqueRequest);

    // let data: ShopResponse;
    // try {
    //   const res = await hotelsApi.apiV0HotelsShopPost({ opaqueRequest });
    //   data = res.data;
    // } catch {
    //   const opaqueRequest = lodging.price.opaqueShopRequest;
    //   // If the initial shop request fails, try to use the `opaqueShopRequest`
    //   // from the availability response.
    //   const res = await hotelsApi.apiV0HotelsShopPost({ opaqueRequest });
    //   shopTokenCache.set(cacheKey, opaqueRequest);
    //   data = res.data;
    // }

    if (shopPayload.Payload === "ShopAvailable") {
      const bestOffer =
        initialResponse.lodgingAvailability.payload.availability.offers
          ?.offersByProductId[lodgingId];
      const availabilityTrackingProperties =
        initialResponse.lodgingAvailability.payload.availability
          .trackingPropertiesV2;

      thunkAPI.dispatch(
        HotelsShopActions.setHotelsShop({
          shop: shopPayload.available,
          search: {
            ...search,
            dateRange: { from: search.fromDate, until: search.untilDate },
            guests: {
              adults: search.adults,
              children: buildLodgingChildrenAges(
                search.children ?? [],
                search.infantsOnLap ?? 0,
                search.infantsInSeat ?? 0
              ),
            },
            rooms: search.roomCount ?? 1,
            lodgingId: lodgingId,
          },
          bestOffer,
          availabilityTrackingProperties,
        })
      );

      return {
        shop: shopPayload.available,
        search,
        bestOffer,
        packageStateByRateId,
        availabilityTrackingProperties,
      };
    } else if (shopPayload.Payload === "ShopUnavailable") {
      thunkAPI.dispatch(HotelsShopActions.setLodgingUnavailable(true));
      return thunkAPI.rejectWithValue({
        error: "Hotel shop unavailable",
      });
    } else {
      return thunkAPI.rejectWithValue({ error: shopPayload.Payload });
    }
  } catch (error) {
    // Reject the thunk with an error message
    return thunkAPI.rejectWithValue({ error: String(error) });
  }
});
